import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Slider,
  TextField,
  Divider
} from '@mui/material';

interface DescontoFormProps {
  title: string;
  podeDescontoItem: boolean | null;
  limiteDescontoItem: number | null;
  podeDescontoTotal: boolean | null;
  limiteDescontoTotal: number | null;
  // AIDEV-NOTE: Novos campos de alteração de preço unitário — migration 009
  podeAlterarPreco: boolean | null;
  limiteReducaoPreco: number | null;
  limiteAumentoPreco: number | null;
  isOverride?: boolean;
  globalValues?: {
    podeDescontoItem: boolean;
    limiteDescontoItem: number;
    podeDescontoTotal: boolean;
    limiteDescontoTotal: number;
    podeAlterarPreco: boolean;
    limiteReducaoPreco: number | null;
    limiteAumentoPreco: number | null;
  };
  onChange: (field: string, value: boolean | number | null) => void;
}

// AIDEV-NOTE: Formulario reutilizado em PoliticasGlobaisPage e VendedorConfigPage
// Quando isOverride=true, campos null = herda do global (mostrar valor global em cinza)
export function DescontoForm({
  title,
  podeDescontoItem,
  limiteDescontoItem,
  podeDescontoTotal,
  limiteDescontoTotal,
  podeAlterarPreco,
  limiteReducaoPreco,
  limiteAumentoPreco,
  isOverride,
  globalValues,
  onChange
}: DescontoFormProps) {
  const effectiveDescontoItem = podeDescontoItem ?? globalValues?.podeDescontoItem ?? false;
  const effectiveLimiteItem = limiteDescontoItem ?? globalValues?.limiteDescontoItem ?? 0;
  const effectiveDescontoTotal = podeDescontoTotal ?? globalValues?.podeDescontoTotal ?? false;
  const effectiveLimiteTotal = limiteDescontoTotal ?? globalValues?.limiteDescontoTotal ?? 0;
  const effectiveAlterarPreco = podeAlterarPreco ?? globalValues?.podeAlterarPreco ?? false;
  const effectiveLimiteReducao = limiteReducaoPreco ?? globalValues?.limiteReducaoPreco ?? null;
  const effectiveLimiteAumento = limiteAumentoPreco ?? globalValues?.limiteAumentoPreco ?? null;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={effectiveDescontoItem}
            onChange={(e) => onChange('pode_dar_desconto_item', e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant="body2">Permite desconto por item</Typography>
            {isOverride && podeDescontoItem === null && (
              <Typography variant="caption" color="text.secondary">
                Padrao global
              </Typography>
            )}
          </Box>
        }
      />

      {effectiveDescontoItem && (
        <Box sx={{ pl: 2, mt: 1, mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Limite de desconto por item: {effectiveLimiteItem}%
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              value={effectiveLimiteItem}
              onChange={(_, v) => onChange('limite_desconto_item', v as number)}
              min={0}
              max={100}
              step={1}
              sx={{ flex: 1 }}
              valueLabelDisplay="auto"
            />
            <TextField
              type="number"
              size="small"
              value={effectiveLimiteItem}
              onChange={(e) =>
                onChange('limite_desconto_item', Math.min(100, Math.max(0, Number(e.target.value))))
              }
              inputProps={{ min: 0, max: 100, style: { width: 50 } }}
              sx={{ width: 80 }}
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      <FormControlLabel
        control={
          <Switch
            checked={effectiveDescontoTotal}
            onChange={(e) => onChange('pode_dar_desconto_total', e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant="body2">Permite desconto total no pedido</Typography>
            {isOverride && podeDescontoTotal === null && (
              <Typography variant="caption" color="text.secondary">
                Padrao global
              </Typography>
            )}
          </Box>
        }
      />

      {effectiveDescontoTotal && (
        <Box sx={{ pl: 2, mt: 1 }}>
          <Typography variant="body2" gutterBottom>
            Limite de desconto total: {effectiveLimiteTotal}%
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Slider
              value={effectiveLimiteTotal}
              onChange={(_, v) => onChange('limite_desconto_total', v as number)}
              min={0}
              max={100}
              step={1}
              sx={{ flex: 1 }}
              valueLabelDisplay="auto"
            />
            <TextField
              type="number"
              size="small"
              value={effectiveLimiteTotal}
              onChange={(e) =>
                onChange(
                  'limite_desconto_total',
                  Math.min(100, Math.max(0, Number(e.target.value)))
                )
              }
              inputProps={{ min: 0, max: 100, style: { width: 50 } }}
              sx={{ width: 80 }}
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      {/* AIDEV-NOTE: Bloco de alteração de preço unitário — toggle + margens independentes */}
      <FormControlLabel
        control={
          <Switch
            checked={effectiveAlterarPreco}
            onChange={(e) => onChange('pode_alterar_preco_unitario', e.target.checked)}
          />
        }
        label={
          <Box>
            <Typography variant="body2">Permite alterar preço unitário do produto</Typography>
            {isOverride && podeAlterarPreco === null && (
              <Typography variant="caption" color="text.secondary">
                Padrao global
              </Typography>
            )}
          </Box>
        }
      />

      {effectiveAlterarPreco && (
        <Box sx={{ pl: 2, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="body2" gutterBottom>
                Limite de redução (%)
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="Vazio = sem limite"
                value={effectiveLimiteReducao ?? ''}
                onChange={(e) => {
                  const v =
                    e.target.value === ''
                      ? null
                      : Math.min(100, Math.max(0, Number(e.target.value)));
                  onChange('limite_reducao_preco_pct', v);
                }}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary">
                Vazio = sem limite para baixo
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="body2" gutterBottom>
                Limite de aumento (%)
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                placeholder="Vazio = sem limite"
                value={effectiveLimiteAumento ?? ''}
                onChange={(e) => {
                  const v =
                    e.target.value === ''
                      ? null
                      : Math.min(999, Math.max(0, Number(e.target.value)));
                  onChange('limite_aumento_preco_pct', v);
                }}
                inputProps={{ min: 0, max: 999, step: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary">
                Vazio = sem limite para cima
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
